/**
 * AiChatArea
 * Main chat area for AI conversations. Uses Vercel AI SDK v5 useChat hook for streaming.
 */

import { useChat } from "@ai-sdk/react"
import type { OnboardingRitual } from "@shared/types"
import { GET_ONBOARDING_RITUAL_PART_TYPE, START_EXPERT_CHAT_PART_TYPE } from "@shared/types"
import { useQueryClient } from "@tanstack/react-query"
import { DefaultChatTransport } from "ai"
import { Archive, ArchiveRestore, ArrowLeft, MoreVertical, Trash2 } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { OnboardingRitualCard } from "@/features/onboarding/components/OnboardingRitualCard"
import { OnboardingShareGuideCTA } from "@/features/onboarding/components/OnboardingShareGuideCTA"
import { OnboardingSuggestionBubbles } from "@/features/onboarding/components/OnboardingSuggestionBubbles"
import { useTranslation } from "@/language"
import { getAccessToken } from "@/lib/token-manager"
import {
  useAiConversation,
  useDeleteAiConversation,
  useUpdateAiConversation,
} from "../../hooks/useAiChat"
import { startExpertChatOutputSchema } from "../../schemas/ai-tools.schema"
import {
  generateCoachIntroPrep,
  generateReflectOpener,
  getMessageStreamUrl,
} from "../../services/ai-chat.service"
import { MessageInput } from "../shared/MessageInput"
import { ChatMessageBubble } from "./ChatMessageBubble"

// Bounded poll for the coach-intro prep when a 409 says a peer claim
// is in flight. We refetch the conversation detail every POLL_MS until
// the metadata flips to "ready" or we hit MAX_POLLS; both numbers are
// sized so a healthy LLM call lands well inside the budget and a stuck
// "generating" status can't pin the typing indicator forever.
const COACH_INTRO_PREP_POLL_MS = 2_000
const COACH_INTRO_PREP_MAX_POLLS = 15
// Reflect opener poll budget deliberately exceeds the backend's 60s stale-claim
// window so a crashed-mid-generation claim can be re-triggered (on the next
// mount) before the client gives up. 40 × 2s = 80s.
const REFLECT_OPENER_POLL_MS = 2_000
const REFLECT_OPENER_MAX_POLLS = 40

function AiChatAreaInner({ id }: { id: string }) {
  const { t } = useTranslation("chats")
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const archiveConversation = useUpdateAiConversation()
  const deleteConversation = useDeleteAiConversation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const firstMessageSent = useRef(false)
  const initialLoadDone = useRef(false)
  const wasStreamingRef = useRef(false)
  // Tool-output parts have no stable id; dedupe by emitted key
  // (conversationId on success, "error:<message>" on failure).
  const navigatedToolConversationIds = useRef(new Set<string>())
  // Tracks the conversation id we've already kicked the coach-intro
  // prep request for. Keyed by id so navigation between two coach
  // chats (without unmount, e.g. SPA route param change) re-arms the
  // effect for the new chat. Resets on transient failures so a user
  // can retry by re-mounting the screen.
  const coachIntroPrepTriggeredFor = useRef<string | null>(null)
  const [isCoachIntroPrepLoading, setIsCoachIntroPrepLoading] = useState(false)
  // Reflect opener: same async-generate flow as coach-intro (see below).
  const reflectOpenerTriggeredFor = useRef<string | null>(null)
  const [isReflectOpenerLoading, setIsReflectOpenerLoading] = useState(false)
  const [inputValue, setInputValue] = useState("")
  // Track which onboarding path the user chose (independent of button text)
  const [practiceChosen, setPracticeChosen] = useState(false)
  const [shareGuideChosen, setShareGuideChosen] = useState(false)
  const shareGuideClickIdxRef = useRef(-1)
  const bottomRef = useRef<HTMLDivElement>(null)

  // Fetch existing conversation data
  const { data: conversationData, isLoading: isConversationLoading } = useAiConversation(id)

  // Create transport for the AI SDK
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getMessageStreamUrl(id),
        headers: () => ({
          Authorization: `Bearer ${getAccessToken()}`,
        }),
      }),
    [id],
  )

  const { messages, sendMessage, status, stop, setMessages, error } = useChat({
    id,
    transport,
  })

  const isLoading = status === "streaming" || status === "submitted"

  // Load existing messages from API into chat state — exactly once per
  // conversation (id changes force a full remount via key={id}). Re-running on
  // status change would overwrite freshly-streamed messages with stale cache
  // and make new turns disappear after the stream completes.
  //
  // Extra guards: the initial fetch can resolve mid-stream when a slow API
  // race against an eager `firstMessage` send, so we also bail if local
  // chat state already has messages or a stream is in flight.
  useEffect(() => {
    if (initialLoadDone.current) return
    if (!conversationData?.messages) return
    if (messages.length > 0) return
    if (status === "streaming" || status === "submitted") return

    if (conversationData.messages.length > 0) {
      const existingMessages = conversationData.messages.map(msg => ({
        id: msg.id,
        role: msg.role as "user" | "assistant",
        parts:
          msg.parts && msg.parts.length > 0
            ? (msg.parts as Array<{ type: "text"; text: string }>)
            : [{ type: "text" as const, text: msg.content }],
      }))
      setMessages(existingMessages)
    }
    initialLoadDone.current = true
  }, [conversationData?.messages, messages.length, setMessages, status])

  useEffect(() => {
    if (messages.length > 0) {
      bottomRef.current?.scrollIntoView({
        behavior: "instant",
        block: "end",
      })
    }
  }, [messages.length])
  // Refresh the conversations cache when a stream finishes so the sidebar
  // reflects new lastMessageAt + previews. Title generation happens in a
  // separate fire-and-forget LLM call after the SSE response closes, so a
  // second invalidation after a short delay picks up the new title without
  // forcing the server to block the response on title generation.
  useEffect(() => {
    const isStreamingNow = status === "streaming" || status === "submitted"
    if (wasStreamingRef.current && !isStreamingNow) {
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
        queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      }
      invalidate()
      const timer = setTimeout(invalidate, 6000)
      wasStreamingRef.current = isStreamingNow
      return () => clearTimeout(timer)
    }
    wasStreamingRef.current = isStreamingNow
  }, [status, queryClient])

  // Coach-intro prep: startCoachChat returns immediately with only the
  // greeting; this effect fires the follow-up LLM call so the trigger
  // page doesn't have to block on it. Renders a typing indicator
  // below the greeting bubble until the prep message arrives.
  //
  // Depends only on the primitive status field so React Query refetches
  // (which return a new metadata object identity even when fields haven't
  // changed) don't re-run the effect body. The ref keyed by id is the
  // second line of defense against StrictMode double-mounts.
  const coachIntroPrepStatus = (
    conversationData?.conversation?.metadata as
      | { type?: string; coachIntroPrepStatus?: "pending" | "generating" | "ready" | string }
      | undefined
  )?.coachIntroPrepStatus
  const conversationType = (
    conversationData?.conversation?.metadata as { type?: string } | undefined
  )?.type
  const reflectOpenerStatus = (
    conversationData?.conversation?.metadata as
      | { reflectOpenerStatus?: "pending" | "generating" | "ready" | string }
      | undefined
  )?.reflectOpenerStatus

  // --- Onboarding-specific UI state ---
  const isOnboarding = conversationType === "onboarding"

  // Path A: ritual card from tool result
  const hasOnboardingRitualCard = messages.some(
    m =>
      m.role === "assistant" &&
      Array.isArray(m.parts) &&
      m.parts.some(
        (p: unknown) =>
          typeof p === "object" &&
          p !== null &&
          (p as Record<string, unknown>).type === GET_ONBOARDING_RITUAL_PART_TYPE &&
          (p as Record<string, unknown>).state === "output-available",
      ),
  )

  // Bubble visibility: hide on bubble click OR after onboarding complete
  const onboardingComplete = hasOnboardingRitualCard || shareGuideChosen
  const hideOnboardingBubbles = practiceChosen || onboardingComplete

  // Path B: Share & guide CTA — count assistant messages after user chose share & guide.
  // The 3rd assistant response (after 2 LLM follow-up questions) is the CTA trigger.
  const shareGuideAssistantCount = useMemo(() => {
    if (!shareGuideChosen || shareGuideClickIdxRef.current === -1) return 0
    return messages.slice(shareGuideClickIdxRef.current).filter(m => m.role === "assistant").length
  }, [messages, shareGuideChosen])

  const showShareGuideCTA =
    isOnboarding &&
    shareGuideChosen &&
    shareGuideAssistantCount >= 3 &&
    !hasOnboardingRitualCard &&
    status === "ready"
  // --- End onboarding state ---

  useEffect(() => {
    if (conversationType !== "coach") return
    // "pending" -> we own the trigger, fire generate.
    // "generating" -> a previous attempt is already running (mid-page-
    // refresh, second tab open, peer in flight). Skip generate, just
    // show the indicator and let the polling effect below pick up the
    // result. The backend's stale-claim recovery (60s) catches the
    // crashed-mid-generation case so we don't pin forever.
    // "ready" -> nothing to do.
    if (coachIntroPrepStatus === "generating") {
      setIsCoachIntroPrepLoading(true)
      return
    }
    if (coachIntroPrepStatus !== "pending") return
    if (coachIntroPrepTriggeredFor.current === id) return

    coachIntroPrepTriggeredFor.current = id
    setIsCoachIntroPrepLoading(true)
    generateCoachIntroPrep(id)
      .then(result => {
        const prep = result.message
        setMessages(prev => [
          ...prev,
          {
            id: prep.id,
            role: "assistant" as const,
            parts: [{ type: "text" as const, text: prep.content }],
          },
        ])
        queryClient.invalidateQueries({ queryKey: ["ai-conversation", id] })
        setIsCoachIntroPrepLoading(false)
      })
      .catch(error => {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 409) {
          // A parallel caller has the claim. The separate polling
          // effect below will keep refetching the conversation detail
          // until the other call flips status to "ready".
          return
        }
        // Anything else (network, 500, timeout) -- reset the trigger ref
        // so a remount can retry, hide the indicator, and surface a toast.
        console.error("Failed to generate coach intro prep", error)
        coachIntroPrepTriggeredFor.current = null
        setIsCoachIntroPrepLoading(false)
        toast.error("Couldn't prepare ritual notes. Try again later.")
      })
  }, [conversationType, coachIntroPrepStatus, id, queryClient, setMessages])

  // While we're waiting for prep to arrive (either we got 409 from a
  // peer claim, or we landed on this page mid-generation after a
  // refresh), poll the conversation detail. The hydration effect picks
  // up the tagged prep from the refetched messages once status flips
  // to "ready". Bounded by MAX_POLLS so a stuck status can't pin the
  // indicator forever; on timeout we surface a toast.
  useEffect(() => {
    if (!isCoachIntroPrepLoading) return
    if (coachIntroPrepStatus === "ready") return

    let polls = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      if (polls >= COACH_INTRO_PREP_MAX_POLLS) {
        setIsCoachIntroPrepLoading(false)
        coachIntroPrepTriggeredFor.current = null
        toast.error("Couldn't prepare ritual notes. Try refreshing the page.")
        return
      }
      polls += 1
      queryClient.invalidateQueries({ queryKey: ["ai-conversation", id] })
      setTimeout(tick, COACH_INTRO_PREP_POLL_MS)
    }
    const timer = setTimeout(tick, COACH_INTRO_PREP_POLL_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isCoachIntroPrepLoading, coachIntroPrepStatus, id, queryClient])

  // When the polling refetch lands a "ready" status, the existing
  // initial-load effect won't pick up the new prep message (it's gated
  // by initialLoadDone). Hydrate any tagged prep that arrived in the
  // refetch into local message state, then drop the indicator.
  useEffect(() => {
    if (!isCoachIntroPrepLoading) return
    if (coachIntroPrepStatus !== "ready") return
    const prep = conversationData?.messages?.find(
      msg =>
        msg.role === "assistant" &&
        Array.isArray(msg.parts) &&
        msg.parts.some(
          part =>
            typeof part === "object" &&
            part !== null &&
            (part as { kind?: unknown }).kind === "coach-intro-prep",
        ),
    )
    if (prep) {
      setMessages(prevMessages => {
        if (prevMessages.some(m => m.id === prep.id)) return prevMessages
        return [
          ...prevMessages,
          {
            id: prep.id,
            role: "assistant" as const,
            parts: [{ type: "text" as const, text: prep.content }],
          },
        ]
      })
      setIsCoachIntroPrepLoading(false)
    }
  }, [isCoachIntroPrepLoading, coachIntroPrepStatus, conversationData?.messages, setMessages])

  // Reflect opener: mirror of the coach-intro flow above. The Reflect button
  // reserves the conversation (no LLM) and navigates immediately; this fires
  // the follow-up LLM call and shows a typing indicator until the opening
  // reflection arrives. Only on-demand-reserved conversations carry
  // reflectOpenerStatus; worker-created ones already have their opener.
  useEffect(() => {
    if (conversationType !== "reflect") return
    // Fire on both "pending" (we own the trigger) and "generating" (a prior
    // attempt is in flight or crashed) — on "generating" the backend returns
    // 409 while fresh, or reclaims a stale (>60s) claim and regenerates, so a
    // stuck conversation can recover on the next mount.
    if (reflectOpenerStatus !== "pending" && reflectOpenerStatus !== "generating") return
    if (reflectOpenerTriggeredFor.current === id) return

    reflectOpenerTriggeredFor.current = id
    setIsReflectOpenerLoading(true)
    let cancelled = false
    generateReflectOpener(id)
      .then(result => {
        if (cancelled) return
        const opener = result.message
        setMessages(prev =>
          prev.some(m => m.id === opener.id)
            ? prev
            : [
                ...prev,
                {
                  id: opener.id,
                  role: "assistant" as const,
                  parts: [{ type: "text" as const, text: opener.content }],
                },
              ],
        )
        queryClient.invalidateQueries({ queryKey: ["ai-conversation", id] })
        setIsReflectOpenerLoading(false)
      })
      .catch(error => {
        if (cancelled) return
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 409) {
          // A parallel caller has the claim; the polling effect below refetches
          // until status flips to "ready".
          return
        }
        console.error("Failed to generate reflection", error)
        reflectOpenerTriggeredFor.current = null
        setIsReflectOpenerLoading(false)
        toast.error("Couldn't start your reflection. Try again later.")
      })
    return () => {
      // Component unmounted / conversation changed before the request settled —
      // don't setState or toast onto a screen the user already left.
      cancelled = true
    }
  }, [conversationType, reflectOpenerStatus, id, queryClient, setMessages])

  useEffect(() => {
    // Keep polling while the indicator is up — do NOT bail purely on
    // status === "ready", since the opener message may not be visible in the
    // same refetch; the hydrate effect below clears the indicator once the
    // tagged message lands.
    if (!isReflectOpenerLoading) return

    let polls = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      if (polls >= REFLECT_OPENER_MAX_POLLS) {
        setIsReflectOpenerLoading(false)
        reflectOpenerTriggeredFor.current = null
        toast.error("Couldn't start your reflection. Try refreshing the page.")
        return
      }
      polls += 1
      queryClient.invalidateQueries({ queryKey: ["ai-conversation", id] })
      setTimeout(tick, REFLECT_OPENER_POLL_MS)
    }
    const timer = setTimeout(tick, REFLECT_OPENER_POLL_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isReflectOpenerLoading, id, queryClient])

  useEffect(() => {
    if (!isReflectOpenerLoading) return
    if (reflectOpenerStatus !== "ready") return
    const opener = conversationData?.messages?.find(
      msg =>
        msg.role === "assistant" &&
        Array.isArray(msg.parts) &&
        msg.parts.some(
          part =>
            typeof part === "object" &&
            part !== null &&
            (part as { kind?: unknown }).kind === "reflect-opener",
        ),
    )
    if (opener) {
      setMessages(prevMessages => {
        if (prevMessages.some(m => m.id === opener.id)) return prevMessages
        return [
          ...prevMessages,
          {
            id: opener.id,
            role: "assistant" as const,
            parts: [{ type: "text" as const, text: opener.content }],
          },
        ]
      })
      setIsReflectOpenerLoading(false)
    }
  }, [isReflectOpenerLoading, reflectOpenerStatus, conversationData?.messages, setMessages])

  // Handle the optional `firstMessage` query param — sent as a real user
  // message so it gets persisted normally. The coach-intro flow has its
  // own greeting + prep path (see effect above) and does not use this
  // param.
  useEffect(() => {
    const firstMessage = searchParams.get("firstMessage")

    if (!id || firstMessageSent.current) return
    if (!firstMessage) return

    firstMessageSent.current = true
    setSearchParams({}, { replace: true })
    sendMessage({ text: firstMessage })
  }, [id, searchParams, setSearchParams, sendMessage])

  // Auto-scroll to bottom on new messages
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll should trigger on every message change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Deep-link follow-through for the startExpertChat tool. The tool's
  // output part lands in message.parts as soon as the backend resolves the
  // booking->conversation lookup; the assistant typically keeps streaming
  // a follow-up text for several seconds after that. Without this effect
  // the user sits on the AI page waiting for the stream to finish while
  // the chat they wanted to open already exists.
  //
  // Watch only the most recent assistant message: during streaming
  // `messages` re-renders ~per token, so scanning every message every
  // tick is O(n*m) wasted work. The tool we care about always emits on
  // the in-flight turn.
  useEffect(() => {
    const last = messages.at(-1)
    if (!last || last.role !== "assistant") return
    const parts = (last as { parts?: unknown }).parts
    if (!Array.isArray(parts)) return

    for (const part of parts) {
      if (!part || typeof part !== "object") continue
      const partRecord = part as { type?: unknown; state?: unknown; output?: unknown }
      if (partRecord.type !== START_EXPERT_CHAT_PART_TYPE) continue
      if (partRecord.state !== "output-available") continue

      const parsed = startExpertChatOutputSchema.safeParse(partRecord.output)
      if (!parsed.success) {
        // Backend tool shape drift. Surface for ops; don't navigate.
        console.error("startExpertChat tool output unexpected shape", parsed.error.format())
        continue
      }

      if (parsed.data.conversationId === null) {
        // Backend tool failed (user has no booking, DB error, etc.).
        // The tool returns a user-safe message; show it once per error
        // string so a stream replay doesn't multi-toast.
        const errorKey = `error:${parsed.data.error}`
        if (navigatedToolConversationIds.current.has(errorKey)) continue
        navigatedToolConversationIds.current.add(errorKey)
        toast.error(parsed.data.error)
        continue
      }

      const { conversationId, deepLink } = parsed.data
      if (navigatedToolConversationIds.current.has(conversationId)) continue
      navigatedToolConversationIds.current.add(conversationId)
      navigate(deepLink)
      return
    }
  }, [messages, navigate])

  // Log stream errors for debugging
  useEffect(() => {
    if (error) {
      console.error("[AiChat] Stream error:", error)
    }
  }, [error])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return
    sendMessage({ text: inputValue })
    setInputValue("")
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="h-[48px] md:h-[64px] px-5 pb-2.5 md:px-[30px] md:py-[17px] flex items-center justify-center md:justify-between border-b border-border">
        <div className="flex items-center gap-2.5 w-full">
          <Button variant="ghost" size="icon-xs" asChild className="md:hidden">
            <Link to="/chats">
              <ArrowLeft className="size-5" />
            </Link>
          </Button>
          <div className="flex items-center gap-2.5">
            <img
              src={aiAvatarImg}
              alt=""
              className="size-[40px] md:size-[30px] rounded-full shrink-0"
            />
            <div className="flex flex-col min-w-0">
              <span className="text-[13px] leading-5 md:text-sm font-semibold text-foreground truncate">
                {(() => {
                  const meta = conversationData?.conversation?.metadata as
                    | { ritualTitle?: string }
                    | null
                    | undefined
                  return meta?.ritualTitle || "My Rituality AI"
                })()}
              </span>
              {(() => {
                const meta = conversationData?.conversation?.metadata as
                  | { type?: string }
                  | null
                  | undefined
                if (meta?.type === "coach") {
                  return (
                    <span className="text-[11px] text-muted-foreground">
                      Personal Rituality Coach
                    </span>
                  )
                }
                if (meta?.type === "reflect") {
                  return (
                    <div className="flex items-center gap-[5px]">
                      <img
                        src={ritualityAiIcon}
                        alt=""
                        className="size-[14px] text-[var(--color-chat-badge)]"
                      />
                      <span className="text-[13px] font-semibold text-[var(--color-chat-badge)]">
                        Rituality AI
                      </span>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("common.moreOptions")}
              className="text-foreground hover:opacity-70 transition-opacity"
              title={t("common.moreOptions")}
            >
              <MoreVertical className="size-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {conversationData?.conversation?.status === "archived" ? (
              <DropdownMenuItem
                onClick={() =>
                  archiveConversation.mutate(
                    { id, data: { status: "active" } },
                    {
                      onSuccess: () => {
                        toast.success("Conversation unarchived")
                      },
                      onError: () => toast.error("Failed to unarchive conversation"),
                    },
                  )
                }
              >
                <ArchiveRestore />
                Unarchive
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() =>
                  archiveConversation.mutate(
                    { id, data: { status: "archived" } },
                    {
                      onSuccess: () => {
                        toast.success("Conversation archived")
                        navigate("/chats")
                      },
                      onError: () => toast.error("Failed to archive conversation"),
                    },
                  )
                }
              >
                <Archive />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                deleteConversation.mutate(id, {
                  onSuccess: () => {
                    toast.success("Conversation deleted")
                    navigate("/chats")
                  },
                  onError: () => toast.error("Failed to delete conversation"),
                })
              }
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 md:px-[30px] py-4 space-y-4">
        {isConversationLoading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="size-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!isConversationLoading && messages.length === 0 && !isLoading && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-text-body opacity-50">
              Send a message to start the conversation
            </p>
          </div>
        )}
        <div className="max-w-[925px] mx-auto flex flex-col gap-5">
          {messages.map(message => (
            <div key={message.id}>
              <ChatMessageBubble message={message} />
              {/* Onboarding: render ritual card from tool-result parts */}
              {message.role === "assistant" &&
                Array.isArray(message.parts) &&
                message.parts.map((part, idx) => {
                  if (
                    typeof part === "object" &&
                    part !== null &&
                    (part as Record<string, unknown>).type === GET_ONBOARDING_RITUAL_PART_TYPE &&
                    (part as Record<string, unknown>).state === "output-available"
                  ) {
                    const output = (part as Record<string, unknown>).output as
                      | { ritual: OnboardingRitual }
                      | undefined
                    if (output?.ritual) {
                      return (
                        <div key={`ritual-card-${idx}`} className="mt-3">
                          <OnboardingRitualCard ritual={output.ritual} />
                        </div>
                      )
                    }
                  }
                  return null
                })}
            </div>
          ))}

          {/* Onboarding: suggestion bubbles below greeting */}
          {isOnboarding && !hideOnboardingBubbles && messages.length > 0 && (
            <OnboardingSuggestionBubbles
              onSelect={text => {
                if (text === "I'm looking for a practice") setPracticeChosen(true)
                if (text === "I want to share & guide others") {
                  setShareGuideChosen(true)
                  shareGuideClickIdxRef.current = messages.length
                }
                sendMessage({ text })
              }}
              disabled={isLoading}
            />
          )}
        </div>
        {error && (
          <div className="flex justify-start">
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400">
              {(() => {
                const msg = String(error.message || "")
                if (msg.includes("401") || msg.includes("403")) {
                  return "Your session may have expired. Please refresh and try again."
                }
                if (msg.includes("429") || /too many requests/i.test(msg)) {
                  return "You're sending messages a bit too fast. Please wait a moment and try again."
                }
                return "Something went wrong. Please try sending your message again."
              })()}
            </div>
          </div>
        )}
        {isLoading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="flex gap-1.5 px-2 py-2">
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:150ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:300ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:450ms]" />
            </div>
          </div>
        )}
        {isCoachIntroPrepLoading && (
          <div className="flex justify-start" aria-label={t("common.preparingNotes")}>
            <div className="flex gap-1.5 px-2 py-2">
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:150ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:300ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:450ms]" />
            </div>
          </div>
        )}
        {isReflectOpenerLoading && (
          <div className="flex justify-start" aria-label={t("common.preparingReflection")}>
            <div className="flex gap-1.5 px-2 py-2">
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:150ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:300ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:450ms]" />
            </div>
          </div>
        )}
        {/* Onboarding: Share & Guide CTA buttons for share & guide path */}
        {showShareGuideCTA && (
          <div className="flex justify-start px-5 md:px-[30px]">
            <OnboardingShareGuideCTA
              onQueueBothTours={async () => {
                const { queueTour } = await import("@/features/tour/services/tour.service")
                await Promise.allSettled([
                  queueTour("home_tour", "on_home_visit"),
                  queueTour("ritual_tour", "on_first_ritual_create"),
                ])
              }}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        input={inputValue}
        onInputChange={setInputValue}
        onSubmit={handleSubmit}
        onStop={stop}
        isLoading={isLoading}
        autoFocus
      />
    </div>
  )
}

export function AiChatArea() {
  const { id } = useParams<{ id: string }>()
  if (!id) return null
  // key={id} forces full remount when conversation changes, resetting all chat state
  return <AiChatAreaInner key={id} id={id} />
}
